<template>
	<div class="customer-page">
		<!-- 顶部操作栏 -->
		<el-card class="action-card" shadow="hover">
			<div class="header-content">
				<h3 class="page-title">客户管理</h3>
				<el-button type="primary" @click="openCreateDialog">
					<el-icon style="margin-right: 5px;"><Plus /></el-icon>
					新增客户
				</el-button>
			</div>
		</el-card>

		<!-- 数据表格 -->
		<el-card class="table-card" shadow="never">
			<el-table 
				:data="customerList" 
				style="width: 100%" 
				v-loading="loading"
				border
				stripe
				:header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
			>
				<el-table-column prop="customerName" label="客户名称" min-width="150" show-overflow-tooltip />
				
				<el-table-column prop="customerPhone" label="客户电话" width="150" align="center">
					<template #default="scope">
						<span class="phone-text" @click="copyPhone(scope.row.customerPhone)" title="点击复制">
							{{ scope.row.customerPhone }}
						</span>
						<el-icon class="copy-icon" @click="copyPhone(scope.row.customerPhone)"><CopyDocument /></el-icon>
					</template>
				</el-table-column>
				
				<el-table-column prop="courierName" label="所属业务员" width="120" align="center" />
				
				<el-table-column prop="remark" label="备注" min-width="200" show-overflow-tooltip />
				
				<el-table-column prop="creationTime" label="创建时间" width="180" align="center" sortable />
				
				<el-table-column label="操作" width="150" align="center" fixed="right">
					<template #default="scope">
						<el-button link type="primary" size="small" @click="handleEdit(scope.row)">
							编辑
						</el-button>
						<el-button link type="danger" size="small" @click="handleDelete(scope.row)">
							删除
						</el-button>
					</template>
				</el-table-column>
			</el-table>
			
			<el-empty v-if="!loading && customerList.length === 0" description="暂无客户数据" />
		</el-card>

		<!-- 新增/编辑 弹窗 -->
		<el-dialog 
			v-model="dialogVisible" 
			:title="isEdit ? '编辑客户' : '新增客户'" 
			width="500px" 
			:close-on-click-modal="false"
			@close="resetForm"
		>
			<el-form 
				ref="formRef" 
				:model="createForm" 
				:rules="rules" 
				label-width="100px"
				class="customer-form"
			>
				<el-form-item label="客户名称" prop="customerName">
					<el-input v-model="createForm.customerName" placeholder="请输入客户名称" clearable />
				</el-form-item>
				
				<el-form-item label="客户电话" prop="customerPhone">
					<el-input v-model="createForm.customerPhone" placeholder="请输入手机号码" maxlength="11" clearable />
				</el-form-item>
				
				<el-form-item label="业务员" prop="courierName">
					<el-input v-model="createForm.courierName" placeholder="请输入负责业务员姓名" clearable />
				</el-form-item>
				
				<el-form-item label="备注" prop="remark">
					<el-input 
						v-model="createForm.remark" 
						type="textarea" 
						:rows="3" 
						placeholder="选填" 
					/>
				</el-form-item>
			</el-form>
			
			<template #footer>
				<span class="dialog-footer">
					<el-button @click="dialogVisible = false">取消</el-button>
					<el-button type="primary" @click="submitForm" :loading="submitLoading">
						确定
					</el-button>
				</span>
			</template>
		</el-dialog>
	</div>
</template>

<script>
	import { Plus, CopyDocument } from '@element-plus/icons-vue';

	export default {
		components: { Plus, CopyDocument },
		data() {
			return {
				loading: false,
				submitLoading: false,
				dialogVisible: false,
				isEdit: false,
				customerList: [],
				createForm: {
					customerName: '',
					customerPhone: '',
					courierName: '',
					remark: ''
				},
				rules: {
					customerName: [
						{ required: true, message: '请输入客户名称', trigger: 'blur' }
					],
					customerPhone: [
						{ required: true, message: '请输入客户电话', trigger: 'blur' },
						{ pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
					]
				}
			}
		},
		methods: {
			// 打开新增弹窗
			openCreateDialog() {
				this.isEdit = false;
				this.dialogVisible = true;
				this.resetForm();
			},
			
			// 重置表单
			resetForm() {
				this.createForm = {
					customerName: '',
					customerPhone: '',
					courierName: '',
					remark: ''
				};
				if (this.$refs.formRef) {
					this.$refs.formRef.clearValidate();
				}
			},

			// 提交表单
			submitForm() {
				this.$refs.formRef.validate((valid) => {
					if (valid) {
						this.submitLoading = true;
						const apiModule = this.isEdit ? "updateCustomer" : "createCustomer";
						
						this.apiService.post({
								modules: apiModule,
								content: { ...this.createForm }
							})
							.then((res) => {
								if (res.status) {
									this.$message.success(this.isEdit ? '修改成功' : '新增成功');
									this.dialogVisible = false;
									this.getCustomerList();
								} else {
									this.$message.error(res.msg || '操作失败');
								}
							})
							.catch(err => {
								console.error(err);
								this.$message.error('网络异常');
							})
							.finally(() => {
								this.submitLoading = false;
							});
					}
				});
			},

			// 获取列表
			getCustomerList() {
				this.loading = true;
				this.apiService.post({
						modules: "getCustomerList",
						content: {}
					})
					.then((res) => {
						if (res.status) {
							this.customerList = res.data.customerList || [];
						}
					})
					.finally(() => {
						this.loading = false;
					});
			},

			// 编辑（预留逻辑）
			handleEdit(row) {
				this.isEdit = true;
				this.createForm = { ...row };
				this.dialogVisible = true;
			},

			// 删除（预留逻辑）
			handleDelete(row) {
				this.$confirm(`确定要删除客户 "${row.customerName}" 吗?`, '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				}).then(() => {
					// 调用删除接口
					this.apiService.post({
						modules: "deleteCustomer",
						content: { id: row.id } // 假设有一个 ID 字段
					}).then(res => {
						if (res.status) {
							this.$message.success('删除成功');
							this.getCustomerList();
						}
					});
				}).catch(() => {});
			},

			// 复制电话
			copyPhone(phone) {
				if (!phone) return;
				navigator.clipboard.writeText(phone).then(() => {
					this.$message.success('电话已复制');
				}).catch(() => {
					this.$message.error('复制失败');
				});
			}
		},
		mounted() {
			this.getCustomerList();
		}
	}
</script>

<style scoped lang="scss">
	.customer-page {
		padding: 20px;
		background-color: #f5f7fa;
		min-height: 100vh;
	}

	.action-card {
		margin-bottom: 20px;
		
		.header-content {
			display: flex;
			justify-content: space-between;
			align-items: center;
			
			.page-title {
				margin: 0;
				font-size: 18px;
				color: #303133;
				font-weight: 600;
			}
		}
	}

	.table-card {
		min-height: 400px;
	}

	.phone-text {
		color: #409eff;
		cursor: pointer;
		margin-right: 5px;
		
		&:hover {
			text-decoration: underline;
		}
	}

	.copy-icon {
		cursor: pointer;
		color: #909399;
		
		&:hover {
			color: #409eff;
		}
	}

	.customer-form {
		padding: 10px 20px;
	}
</style>